/**
 * 子パターンJSON取り込みの検証（#3372）。
 *
 * 既存の validateSubPattern（名称・説明・上書き件数）は内部から呼び、重複実装しない。
 * ここで追加するのは「JSONだから起こりうる誤り」の検証である。
 *
 * とくに重要なのが項目IDの存在チェック。
 * OCR 実行時の applySubPattern は親に存在しない item_id を黙って無視するため
 * （親から項目を削除しても子パターンを書き換えずに済ませる遅延解決）、
 * 存在しないIDの上書きは**保存できても一切効かない**。
 * 画面上は「上書き済み」に見えるのに読み取りが変わらず原因の特定が難しいので、
 * 実行時の寛容さは変えずに、取り込みの入り口で弾く。
 */

import { subPatternErrorMessage } from '../../constants/subPattern';
import type { SubPatternType } from '../../types/subPattern';
import type { SubPatternOverrideCandidate } from '../../components/uiParts/modal/SubPatternEditModal';
import { validateSubPattern } from './subPatternValidation';
import { toItemOverrides } from './subPatternJson';
import {
  allowedSubPatternKeys,
  allowedOverrideKeys,
  type SubPatternJsonType,
} from './subPatternJson';

export const subPatternJsonErrorMessage = {
  invalidFormat: 'JSONの形式が正しくありません。',
  notObjectOrArray: 'JSONはオブジェクトまたは配列形式で入力してください。',
  // 単体の編集では1件しか反映できない（#3372）。
  // 複数件をまとめて登録したい場合はパターン一覧の一括登録を使う
  singleOnly:
    'この画面では1件の子パターンだけ編集できます。複数件をまとめて登録する場合は、読取パターン一覧から行ってください。',
  overridesNotArray: '"item_overrides" は配列形式で入力してください。',
  emptyItemId: '各上書きに "item_id" が必要です。',
  itemIdNotFound: (itemId: string): string =>
    `項目ID "${itemId}" はこの読取パターンに存在しません。`,
  duplicatedItemId: (itemId: string): string => `項目ID "${itemId}" が重複しています。`,
  notOverridable: (itemName: string): string =>
    `項目 "${itemName}" は子パターンで上書きできません。`,
  ocrTargetNotAllowed: (itemName: string): string =>
    `明細項目 "${itemName}" にAI読取のON/OFFは指定できません。列ごとに指定してください。`,
  fixedValueNotAllowed: (itemName: string): string =>
    `明細項目 "${itemName}" に固定値は指定できません。列ごとに指定してください。`,
  unknownKey: (key: string): string => `"${key}" は子パターンでは指定できません。`,
  duplicatedNameInJson: (name: string): string =>
    `JSON内で子パターンの名称 "${name}" が重複しています。`,
} as const;

type ValidateInput = {
  /** 正規化済みのJSON */
  items: SubPatternJsonType[];
  /** 正規化前の生オブジェクト。想定外のキーを検出するために使う */
  raws: Record<string, unknown>[];
  /** 親パターンから組み立てた上書き候補 */
  candidates: SubPatternOverrideCandidate[];
  /** 同一パターンの既存の子パターン。名称の重複判定に使う */
  existingSubPatterns: SubPatternType[];
  /** 編集中の子パターンID（単体取り込みのとき。自分自身を重複判定から除く） */
  editingSubPatternId?: string;
};

/**
 * 想定外のキーを探す。
 *
 * 子パターンが上書きできるのは取得ルール・固定値・項目名・AI読取のON/OFFだけ（#2967）。
 * 文字数制限や children のような対象外のキーを黙って捨てると、
 * 「JSONに書いたのに効かない」状態になるため明示的に弾く。
 */
const findUnknownKey = (raw: Record<string, unknown>): string | null => {
  for (const key of Object.keys(raw)) {
    if (!allowedSubPatternKeys.includes(key)) return key;
  }
  const overrides = raw.item_overrides ?? raw.itemOverrides;
  if (Array.isArray(overrides)) {
    for (const override of overrides) {
      if (typeof override !== 'object' || override === null) continue;
      for (const key of Object.keys(override as Record<string, unknown>)) {
        if (!allowedOverrideKeys.includes(key)) return key;
      }
    }
  }
  return null;
};

/** 1件ぶんの上書きを検証する */
const validateOverrides = (
  item: SubPatternJsonType,
  candidates: SubPatternOverrideCandidate[],
): string | null => {
  const candidateMap = new Map(candidates.map(c => [c.itemId, c]));
  const seen = new Set<string>();

  for (const override of item.item_overrides) {
    if (override.item_id === '') return subPatternJsonErrorMessage.emptyItemId;

    if (seen.has(override.item_id)) {
      return subPatternJsonErrorMessage.duplicatedItemId(override.item_id);
    }
    seen.add(override.item_id);

    const candidate = candidateMap.get(override.item_id);
    // 候補に無い＝親パターンに存在しない、または上書き対象外（非活性・計算フィールド）。
    // どちらもIDからは区別できないため、存在しない旨のメッセージにそろえる
    if (!candidate) {
      return subPatternJsonErrorMessage.itemIdNotFound(override.item_id);
    }

    if (override.is_ocr_target !== undefined && !candidate.canToggleOcrTarget) {
      return subPatternJsonErrorMessage.ocrTargetNotAllowed(candidate.itemName);
    }
    if (override.fixed_value !== undefined && !candidate.canSetFixedValue) {
      return subPatternJsonErrorMessage.fixedValueNotAllowed(candidate.itemName);
    }
  }

  return null;
};

/**
 * JSON取り込みの検証。エラーがなければ null を返す（validateSubPattern と同じ流儀）。
 *
 * 一括取り込みでは1件でもエラーがあれば何も登録しないため、
 * 最初に見つかったエラーを返して呼び出し側で止める。
 */
export const validateSubPatternJson = (input: ValidateInput): string | null => {
  const { items, raws, candidates, existingSubPatterns, editingSubPatternId } = input;

  const namesInJson = new Set<string>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const raw = raws[i];

    // 対象外のキーが混ざっていないか
    const unknownKey = findUnknownKey(raw);
    if (unknownKey) return subPatternJsonErrorMessage.unknownKey(unknownKey);

    // item_overrides が配列でない場合（正規化で空配列になるため生データを見る）
    const rawOverrides = raw.item_overrides ?? raw.itemOverrides;
    if (rawOverrides !== undefined && !Array.isArray(rawOverrides)) {
      return subPatternJsonErrorMessage.overridesNotArray;
    }

    // JSON内での名称重複。一括取り込みで同名が2件あると、
    // 1件目を作ってから2件目で更新に回り、意図と違う結果になる
    if (namesInJson.has(item.sub_pattern_name)) {
      return subPatternJsonErrorMessage.duplicatedNameInJson(item.sub_pattern_name);
    }
    namesInJson.add(item.sub_pattern_name);

    const overrideError = validateOverrides(item, candidates);
    if (overrideError) return overrideError;

    // 名称・説明・上書き件数は既存の検証を使う。
    // 一括取り込みでは、同名の既存子パターンは「更新対象」なので重複判定から除く
    const matchedExisting = existingSubPatterns.find(
      sub => (sub.sub_pattern_name ?? '').trim() === item.sub_pattern_name,
    );
    const baseError = validateSubPattern({
      subPatternName: item.sub_pattern_name,
      description: item.description,
      itemOverrides: toItemOverrides(item.item_overrides),
      existingSubPatterns,
      editingSubPatternId: editingSubPatternId ?? matchedExisting?.subPatternId,
    });
    if (baseError) return baseError;
  }

  if (items.length === 0) return subPatternErrorMessage.emptyOverrides;

  return null;
};
